import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Hex } from 'ox'
import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { Amount, Box, Button, Empty, Rows, Token, Tooltip } from 'regen-ui'
import { toast } from 'sonner'
import { formatUnits } from 'viem'
import { useConnection } from 'wagmi'
import { tempo } from 'wagmi/chains'
import ArrowDownToLine from '~icons/lucide/arrow-down-to-line'
import ArrowLeftRight from '~icons/lucide/arrow-left-right'
import ArrowUpFromLine from '~icons/lucide/arrow-up-from-line'
import ArrowUpRight from '~icons/lucide/arrow-up-right'
import ChevronDown from '~icons/lucide/chevron-down'
import Globe from '~icons/lucide/globe'
import Lock from '~icons/lucide/lock'
import SendHorizontal from '~icons/lucide/send-horizontal'
import VenetianMask from '~icons/lucide/venetian-mask'
import Wallet from '~icons/lucide/wallet'

import * as Activity from '#/lib/activity.js'
import * as Assets from '#/lib/assets.js'
import { useAssets } from '#/lib/assets.js'
import { useDialogConnector } from '#/lib/connectors.js'
import * as Valuation from '#/lib/valuation.js'
import * as Zone from '#/lib/zone.js'
import {
  PrivateBalancesButton,
  PrivateBalancesTag,
  usePrivateBalances,
} from '#/routes/_app/-components/PrivateBalancesControls.js'
import { ScreenLayout } from '#/routes/_app/-components/ScreenLayout.js'

export const Route = createFileRoute('/_app/balances')({
  component: BalancesPage,
})

const money = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  style: 'currency',
})

const rowHeight = 48
const expandedRowHeight = 36

function BalancesPage() {
  const connection = useConnection()
  const { address, chainId } = connection
  const usdToken =
    chainId === tempo.id
      ? '0x20C000000000000000000000b9537d11c60E8b50'
      : '0x20c0000000000000000000000000000000000000'

  const assets = useAssets({
    account: address,
    chainId,
    enabled: typeof window !== 'undefined' && !!address,
    fresh: true,
    tokens: [usdToken],
  })
  const valuation = Valuation.useValuation({ account: address, chainId })

  const total = useMemo(
    () =>
      Valuation.total({
        assets: assets.data ?? [],
        fallback: (assets.data ?? []).reduce(
          (sum, asset) => sum + Number(formatUnits(grossBalance(asset), asset.decimals)),
          0,
        ),
        valuation: valuation.data,
      }),
    [assets.data, valuation.data?.amount, valuation.data?.available],
  )

  const verified = useMemo(
    () =>
      (assets.data ?? [])
        .filter((a) => grossBalance(a) > 0n && a.verified !== false)
        .slice()
        .sort(compareGross),
    [assets.data],
  )

  const unverified = useMemo(
    () =>
      (assets.data ?? [])
        .filter((a) => grossBalance(a) > 0n && a.verified === false)
        .slice()
        .sort(compareGross),
    [assets.data],
  )

  const [showAll, setShowAll] = useState(false)
  const privateBalances = usePrivateBalances()
  const alignTrailing =
    verified.some((asset) => asset.zones?.length) ||
    (showAll && unverified.some((asset) => asset.zones?.length))

  return (
    <ScreenLayout
      actions={
        privateBalances ? (
          <PrivateBalancesButton className="shrink-0 max-sm:px-3" privacy={privateBalances} />
        ) : null
      }
      heading={
        <>
          <div className="flex items-center gap-2">
            <h1 className="text-heading-24">Balances</h1>
            <PrivateBalancesTag privacy={privateBalances} />
          </div>
          <p
            className="text-heading-32 tabular-nums min-[880px]:text-heading-48"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {assets.isLoading ? (
              <span className="inline-block h-[1em] w-48 animate-pulse rounded-body bg-skeleton align-middle" />
            ) : (
              money.format(total)
            )}
          </p>
        </>
      }
    >
      <Box
        className="flex flex-col max-sm:-mx-4 sm:-mx-8 md:mx-0 max-md:rounded-none max-md:border-x-0"
        padding={0}
      >
        <Rows className="pb-3 [&_[data-rows-divider]]:hidden" variant="surface">
          {assets.isLoading ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : verified.length === 0 && (!showAll || unverified.length === 0) ? (
            <Empty
              action={
                <Button.Text
                  render={
                    <a href="https://docs.tempo.xyz" rel="noopener noreferrer" target="_blank" />
                  }
                  suffix={<ArrowUpRight className="size-3.5" />}
                >
                  Learn more
                </Button.Text>
              }
              icon={<Wallet />}
              title="No balances yet"
            >
              Deposit funds to start using your wallet.
            </Empty>
          ) : (
            <>
              {verified.map((asset) => (
                <BalanceGroup
                  alignTrailing={alignTrailing}
                  asset={asset}
                  chainId={chainId!}
                  key={asset.address}
                />
              ))}
              {showAll &&
                unverified.map((asset) => (
                  <BalanceGroup
                    alignTrailing={alignTrailing}
                    asset={asset}
                    chainId={chainId!}
                    key={asset.address}
                  />
                ))}
              {!showAll && unverified.length > 0 && (
                <Rows.Row
                  className="text-label-14 text-foreground-secondary transition-colors hover:text-foreground"
                  height={40}
                  onClick={() => setShowAll(true)}
                >
                  View unverified assets
                </Rows.Row>
              )}
            </>
          )}
        </Rows>
      </Box>
    </ScreenLayout>
  )
}

function BalanceGroup(props: { alignTrailing: boolean; asset: useAssets.Asset; chainId: number }) {
  const { alignTrailing, asset, chainId } = props
  const [expanded, setExpanded] = useState(false)
  const zones = asset.zones ?? []
  const hasZones = zones.length > 0
  const gross = grossBalance(asset)

  const zoneSum = zones.reduce((sum, z) => sum + BigInt(z.balance), 0n)
  const hasPrivate = zoneSum > 0n

  // No zones → plain row, no expand machinery.
  if (!hasZones)
    return (
      <BalanceRow
        alignTrailing={alignTrailing}
        asset={asset}
        chainId={chainId}
        gross={gross}
        hasPrivate={hasPrivate}
      />
    )

  return (
    <>
      <BalanceRow
        alignTrailing={alignTrailing}
        asset={asset}
        chainId={chainId}
        expanded={expanded}
        gross={gross}
        hasPrivate={hasPrivate}
        onClick={() => setExpanded((open) => !open)}
      />
      {expanded && (
        <>
          <PrivacyRow asset={asset} balance={BigInt(asset.balance)} icon={Globe} label="Public" />
          <PrivacyRow actions asset={asset} balance={zoneSum} icon={Lock} label="Private" />
        </>
      )}
    </>
  )
}

function grossBalance(asset: useAssets.Asset) {
  return (asset.zones ?? []).reduce((sum, z) => sum + BigInt(z.balance), BigInt(asset.balance))
}

function BalanceRow(props: {
  alignTrailing: boolean
  asset: useAssets.Asset
  chainId: number
  expanded?: boolean | undefined
  gross: bigint
  hasPrivate?: boolean
  onClick?: (() => void) | undefined
}) {
  const { alignTrailing, asset, chainId, expanded, gross, hasPrivate, onClick } = props
  const connector = useDialogConnector()
  const queryClient = useQueryClient()
  const { address: account } = useConnection()
  const swappable = asset.address.toLowerCase().startsWith('0x20c0')

  const swap = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      await provider.request({
        method: 'wallet_swap',
        params: [{ token: asset.address, type: 'sell' }],
      } as never)
    },
    onSuccess: () => {
      toast.success('Swap confirmed.')
      if (account) {
        queryClient.invalidateQueries({ queryKey: Assets.queryKey(account) })
        queryClient.invalidateQueries({ queryKey: Activity.queryKey(account) })
      }
    },
  })

  const send = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      return (await provider.request({
        method: 'wallet_transfer',
        params: [{ editable: true, token: asset.address }],
      } as never)) as { chainId: number; receipt: { logs: readonly Assets.setReceiptData.Log[] } }
    },
    onSuccess: (result) => {
      toast.success('Transaction confirmed.')
      if (account)
        Assets.setReceiptData({
          account,
          chainId: result.chainId,
          queryClient,
          receipt: result.receipt,
        })
      if (account) queryClient.invalidateQueries({ queryKey: Activity.queryKey(account) })
    },
  })

  const amount = (
    <Amount
      align="right"
      amount={{
        amount: Hex.fromNumber(gross),
        decimals: asset.decimals,
        formatted: formatUnits(gross, asset.decimals),
        symbol: asset.symbol,
      }}
      size="md"
    />
  )

  const actions = (
    <div className="flex items-center justify-end gap-0" onClick={(e) => e.stopPropagation()}>
      <Tooltip label={swappable ? `Swap ${asset.symbol}` : `${asset.symbol} cannot be swapped`}>
        <Button.Icon
          aria-label={swappable ? `Swap ${asset.symbol}` : `${asset.symbol} cannot be swapped`}
          className="disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:opacity-25"
          disabled={!swappable}
          onClick={() => swap.mutate()}
          size="md"
          variant="plain"
        >
          <ArrowLeftRight />
        </Button.Icon>
      </Tooltip>
      <Tooltip label={`Send ${asset.symbol}`}>
        <Button.Icon
          aria-label={`Send ${asset.symbol}`}
          className="-ml-2"
          onClick={() => send.mutate()}
          size="md"
          variant="plain"
        >
          <SendHorizontal />
        </Button.Icon>
      </Tooltip>
    </div>
  )

  const trailing = (() => {
    if (alignTrailing)
      return (
        <BalanceTrailing
          actions={actions}
          amount={amount}
          expanded={expanded}
          onClick={onClick}
          symbol={asset.symbol}
        />
      )

    return (
      <div className="flex shrink-0 items-center gap-0">
        {amount}
        {actions}
      </div>
    )
  })()

  const content = (
    <>
      <Token address={asset.address} chainId={chainId} className="size-6" />
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <p className="truncate text-label-14 text-foreground">{asset.name}</p>
        {hasPrivate && (
          <Tooltip label="Has private balance">
            <VenetianMask
              aria-label="Has private balance"
              className="size-3.5 text-foreground-secondary"
            />
          </Tooltip>
        )}
      </div>
      {trailing}
    </>
  )

  return <Rows.Row height={rowHeight}>{content}</Rows.Row>
}

function BalanceTrailing(props: {
  actions: ReactElement
  amount: ReactElement
  expanded?: boolean | undefined
  onClick?: (() => void) | undefined
  symbol?: string | undefined
}) {
  const { actions, amount, expanded, onClick, symbol } = props
  const label = (() => {
    if (!symbol || expanded === undefined) return undefined
    if (expanded) return `Hide locations for ${symbol}`
    return `Show locations for ${symbol}`
  })()

  return (
    <div className="flex w-48 shrink-0 items-center justify-end gap-1 min-[380px]:w-52 sm:w-64 sm:gap-2">
      <div className="flex min-w-0 flex-1 justify-end">{amount}</div>
      <div className="flex w-14 justify-end sm:w-[72px]">{actions}</div>
      <div className="flex w-8 justify-center">
        {label && onClick ? (
          <Tooltip label={label}>
            <Button.Icon
              aria-expanded={expanded}
              aria-label={label}
              onClick={onClick}
              size="md"
              variant="plain"
            >
              <ChevronDown
                aria-hidden="true"
                className="size-4 transition-transform data-[expanded=true]:rotate-180"
                data-expanded={expanded}
              />
            </Button.Icon>
          </Tooltip>
        ) : (
          <div aria-hidden className="size-8" />
        )}
      </div>
    </div>
  )
}

function PrivacyRow(props: {
  /** When true, render deposit + withdraw action buttons in the trailing icon slot. */
  actions?: boolean | undefined
  asset: useAssets.Asset
  balance: bigint
  icon: React.ComponentType<{ className?: string }>
  label: string
}) {
  const { actions, asset, balance, icon: Icon, label } = props
  const canWithdraw = balance > 0n
  const connector = useDialogConnector()
  const queryClient = useQueryClient()
  const { address: account, chainId: parentChainId } = useConnection()

  const deposit = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      const result = (await provider.request({
        method: 'wallet_depositZone',
        params: [{ token: asset.address }],
      } as never)) as { receipt: { logs: readonly Assets.setReceiptData.Log[] } }
      return result.receipt
    },
    onSuccess: (receipt) => {
      toast.success('Deposit confirmed.')
      if (account && parentChainId)
        Assets.setReceiptData({ account, chainId: parentChainId, queryClient, receipt })
      if (account) queryClient.invalidateQueries({ queryKey: Activity.queryKey(account) })
    },
  })

  const withdraw = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      const result = (await provider.request({
        method: 'wallet_withdrawZone',
        params: [{ token: asset.address }],
      } as never)) as { receipt: { logs: readonly Assets.setReceiptData.Log[] } }
      return result.receipt
    },
    onSuccess: (receipt) => {
      toast.success('Withdrawal submitted.')
      if (account) Assets.setReceiptData({ account, chainId: Zone.chain.id, queryClient, receipt })
      if (account) queryClient.invalidateQueries({ queryKey: Activity.queryKey(account) })
    },
  })

  return (
    <Rows.Row className="text-foreground-secondary" height={expandedRowHeight}>
      <div aria-hidden className="flex size-6 shrink-0 items-center justify-center">
        <Icon className="size-3.5" />
      </div>
      <p className="min-w-0 flex-1 truncate text-label-14">{label}</p>
      <BalanceTrailing
        actions={
          <div className="flex items-center justify-end gap-0">
            {actions ? (
              <>
                <Tooltip label="Deposit to private balance">
                  <Button.Icon
                    aria-label="Deposit to private balance"
                    onClick={() => deposit.mutate()}
                    size="md"
                    variant="plain"
                  >
                    <ArrowDownToLine />
                  </Button.Icon>
                </Tooltip>
                {canWithdraw ? (
                  <Tooltip label="Withdraw from private balance">
                    <Button.Icon
                      aria-label="Withdraw from private balance"
                      className="-ml-2"
                      onClick={() => withdraw.mutate()}
                      size="md"
                      variant="plain"
                    >
                      <ArrowUpFromLine />
                    </Button.Icon>
                  </Tooltip>
                ) : (
                  <div aria-hidden className="-ml-2 size-8" />
                )}
              </>
            ) : (
              <>
                <div aria-hidden className="size-8" />
                <div aria-hidden className="-ml-2 size-8" />
              </>
            )}
          </div>
        }
        amount={
          <Amount
            align="right"
            amount={{
              amount: Hex.fromNumber(balance),
              decimals: asset.decimals,
              formatted: formatUnits(balance, asset.decimals),
              symbol: asset.symbol,
            }}
            size="md"
          />
        }
      />
    </Rows.Row>
  )
}

function SkeletonRow() {
  return (
    <Rows.Row height={rowHeight}>
      <div className="size-6 animate-pulse rounded-identicon bg-skeleton" />
      <div className="h-4 w-20 animate-pulse rounded-body bg-skeleton" />
      <div className="ml-auto h-4 w-16 animate-pulse rounded-body bg-skeleton" />
    </Rows.Row>
  )
}

/** Highest gross balance first; ties break alphabetically by symbol so the order is stable across renders. */
function compareGross(a: useAssets.Asset, b: useAssets.Asset) {
  const av = Number(formatUnits(grossBalance(a), a.decimals))
  const bv = Number(formatUnits(grossBalance(b), b.decimals))
  if (av === bv) return a.symbol.localeCompare(b.symbol)
  return bv - av
}
